import { useMemo } from "react";
import { parsePlainIntegerCodeText } from "@common/helpers/accounting";
import { DraftGroup } from "./draftOperations";
import { GetRowCapabilities } from "./editorCapabilities";
import { ChartOfAccountsTemplateEditorRow } from "./utils";

export type EditorIssueKind =
    | "blankName"
    | "duplicateName"
    | "duplicateCode"
    | "blankGroupName"
    | "duplicateGroupName";

export interface EditorIssue {
    rowId: string;
    field: "name" | "code";
    kind: EditorIssueKind;
    message: string;
}

interface BuildTemplateEditorIssuesParams {
    // The full, ordered row list (not the search-filtered subset) so every
    // blocking issue is reachable from the navigation bar.
    rows: ChartOfAccountsTemplateEditorRow[];
    invalidNameRowIds: Set<string>;
    duplicateNameRowIds: Set<string>;
    duplicateCodeIds: Set<string>;
    getRowCapabilities: GetRowCapabilities;
    // Custom groups + their blank / duplicate name ids, so group-name issues are
    // surfaced in the same navigable bar.
    groups: DraftGroup[];
    invalidGroupNameIds: Set<string>;
    duplicateGroupNameIds: Set<string>;
}

type RowsByCode = Map<number, ChartOfAccountsTemplateEditorRow[]>;

// How the account is referred to in a message: its name, else "Account <code>",
// else a generic fallback.
const buildDisplayName = (row: ChartOfAccountsTemplateEditorRow): string => {
    if (row.name.trim() !== "") {
        return row.name;
    }
    if (row.codeText != null) {
        return `Account ${row.codeText}`;
    }
    return "This account";
};

const buildBlankNameMessage = (
    row: ChartOfAccountsTemplateEditorRow,
): string => {
    if (row.codeText != null) {
        return `Account ${row.codeText} needs a name`;
    }
    return "This account needs a name";
};

// The first OTHER row (different id) sharing this row's parsed code.
const findDuplicateCounterpart = (
    row: ChartOfAccountsTemplateEditorRow,
    rowsByCode: RowsByCode,
): ChartOfAccountsTemplateEditorRow | undefined => {
    const parsed = parsePlainIntegerCodeText(row.codeText);
    if (parsed == null) {
        return undefined;
    }
    const sharingRows = rowsByCode.get(parsed) ?? [];
    return sharingRows.find((other) => other.id !== row.id);
};

const buildDuplicateCodeMessage = (
    row: ChartOfAccountsTemplateEditorRow,
    rowsByCode: RowsByCode,
): string => {
    const counterpart = findDuplicateCounterpart(row, rowsByCode);
    const otherDisplay = counterpart
        ? buildDisplayName(counterpart)
        : "another account";
    return `Code ${row.codeText} is already used by ${otherDisplay}`;
};

interface ResolveCodeIssueParams {
    row: ChartOfAccountsTemplateEditorRow;
    duplicateCodeIds: Set<string>;
    rowsByCode: RowsByCode;
}

const resolveCodeIssue = ({
    row,
    duplicateCodeIds,
    rowsByCode,
}: ResolveCodeIssueParams): EditorIssue | null => {
    if (duplicateCodeIds.has(row.id)) {
        return {
            rowId: row.id,
            field: "code",
            kind: "duplicateCode",
            message: buildDuplicateCodeMessage(row, rowsByCode),
        };
    }
    return null;
};

// Indexes every parseable code to the rows carrying it so duplicate-counterpart
// lookups stay O(1) per row (overall O(n)).
const buildRowsByCode = (
    rows: ChartOfAccountsTemplateEditorRow[],
): RowsByCode => {
    const rowsByCode: RowsByCode = new Map();
    for (const row of rows) {
        const parsed = parsePlainIntegerCodeText(row.codeText);
        if (parsed == null) {
            continue;
        }
        const existing = rowsByCode.get(parsed);
        if (existing) {
            existing.push(row);
        } else {
            rowsByCode.set(parsed, [row]);
        }
    }
    return rowsByCode;
};

// At most one name issue per custom group, in priority order: blank > duplicate
// (a blank name is never counted as a duplicate). Mirrors the group name cell's
// styling, which shows the yellow "Add name" affordance for blank and the red
// error state for a duplicate.
const buildGroupIssues = ({
    groups,
    invalidGroupNameIds,
    duplicateGroupNameIds,
}: {
    groups: DraftGroup[];
    invalidGroupNameIds: Set<string>;
    duplicateGroupNameIds: Set<string>;
}): EditorIssue[] => {
    const issues: EditorIssue[] = [];
    for (const group of groups) {
        if (invalidGroupNameIds.has(group.id)) {
            issues.push({
                rowId: group.id,
                field: "name",
                kind: "blankGroupName",
                message: "Group needs a name",
            });
        } else if (duplicateGroupNameIds.has(group.id)) {
            issues.push({
                rowId: group.id,
                field: "name",
                kind: "duplicateGroupName",
                message: `A group named "${group.name.trim()}" already exists here`,
            });
        }
    }
    return issues;
};

// Builds the ordered, save-blocking issue list the editor's footer navigates.
// Rows are walked in display order; within a row a name issue precedes its code
// issue. Placeholder rows legitimately carry null codes and are skipped
// entirely. Each offending (row, field) is its own entry — messages are
// per-account. Custom group name issues are appended after the account issues.
export const buildTemplateEditorIssues = ({
    rows,
    invalidNameRowIds,
    duplicateNameRowIds,
    duplicateCodeIds,
    getRowCapabilities,
    groups,
    invalidGroupNameIds,
    duplicateGroupNameIds,
}: BuildTemplateEditorIssuesParams): EditorIssue[] => {
    const rowsByCode = buildRowsByCode(rows);
    const issues: EditorIssue[] = [];
    for (const row of rows) {
        if (getRowCapabilities(row.account).placeholder != null) {
            continue;
        }
        if (invalidNameRowIds.has(row.id)) {
            issues.push({
                rowId: row.id,
                field: "name",
                kind: "blankName",
                message: buildBlankNameMessage(row),
            });
        } else if (duplicateNameRowIds.has(row.id)) {
            issues.push({
                rowId: row.id,
                field: "name",
                kind: "duplicateName",
                message: `An account named "${row.name.trim()}" already exists here`,
            });
        }
        const codeIssue = resolveCodeIssue({
            row,
            duplicateCodeIds,
            rowsByCode,
        });
        if (codeIssue) {
            issues.push(codeIssue);
        }
    }
    issues.push(
        ...buildGroupIssues({
            groups,
            invalidGroupNameIds,
            duplicateGroupNameIds,
        }),
    );
    return issues;
};

export const useTemplateEditorIssues = ({
    rows,
    invalidNameRowIds,
    duplicateNameRowIds,
    duplicateCodeIds,
    getRowCapabilities,
    groups,
    invalidGroupNameIds,
    duplicateGroupNameIds,
}: BuildTemplateEditorIssuesParams): EditorIssue[] =>
    useMemo(
        () =>
            buildTemplateEditorIssues({
                rows,
                invalidNameRowIds,
                duplicateNameRowIds,
                duplicateCodeIds,
                getRowCapabilities,
                groups,
                invalidGroupNameIds,
                duplicateGroupNameIds,
            }),
        [
            rows,
            invalidNameRowIds,
            duplicateNameRowIds,
            duplicateCodeIds,
            getRowCapabilities,
            groups,
            invalidGroupNameIds,
            duplicateGroupNameIds,
        ],
    );

// A host that cannot widen its business filter cannot navigate to an issue in
// a business it does not display. Filtering the built list, rather than the
// rows, leaves a duplicate-code message naming its counterpart elsewhere.
export const listNavigableEditorIssues = ({
    issues,
    onEntityFilterReset,
    isRowBusinessHidden,
}: {
    issues: EditorIssue[];
    onEntityFilterReset: (() => void) | undefined;
    isRowBusinessHidden: (rowId: string) => boolean;
}): EditorIssue[] => {
    if (onEntityFilterReset) {
        return issues;
    }
    return issues.filter((issue) => !isRowBusinessHidden(issue.rowId));
};
